/**
 * Shared Firebase Client Initialization
 * Used across all frontend applications (admin, dash, landing)
 */

import { getApp, getApps, initializeApp, FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getAnalytics } from "firebase/analytics";
import { getPerformance } from "firebase/performance";

declare global {
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
  databaseURL: string;
}

export interface FirebaseInitOptions {
  config: FirebaseConfig;
  recaptchaSiteKey: string;
  isDebug: boolean;
  fetchRemoteConfig?: () => Promise<void>;
}

/**
 * Initialize Firebase app with AppCheck, Analytics, and Performance monitoring
 * @param options - Firebase initialization options
 * @returns Promise that resolves when initialization is complete
 */
export const initFirebase = async (options: FirebaseInitOptions): Promise<FirebaseApp> => {
  const { config, recaptchaSiteKey, isDebug, fetchRemoteConfig } = options;

  if (!getApps().length) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = isDebug;
    
    const app = initializeApp(config);
    
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    
    getAnalytics(app);
    getPerformance(app);
    
    if (fetchRemoteConfig) {
      await fetchRemoteConfig();
    }
    
    return app;
  } else {
    return getApp();
  }
};
